import { useWeb3React } from '@web3-react/core'
import Big from 'big.js'
import { createMETLocker } from 'metronome-esmet-lib'
import { useChainId } from 'metronome-ui/hooks/chains'
import { fromUnit } from 'metronome-utils'
import { findTokenByAddress } from 'token-list'
import { useRouter } from 'next/router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import useSWR from 'swr'

import useBoostedRewards, {
  RewardsApiResponse
} from '../hooks/useBoostedRewards'
import useLockMETOperationsDrawer from '../hooks/useLockMETOperationsDrawer'
import {
  UseOperationsDrawerCloseHandler,
  UseOperationsDrawerOpenHandler
} from '../hooks/useOperationsDrawer'
import { useWalletChainId } from '../hooks/useWalletChainId'
import { fetcher } from '../utils'

import ErrorComponent from './ErrorComponent'
import { MetronomeContext } from './MetronomeContext'
import { UnlockFormProps } from './UnlockForm'

export type LockPosition = {
  boostedAmount: string
  lockedAmount: string
  lockedAmountInUsd: string
  lockedPeriod: number
  minimumUnlockTime: number
  tokenId: number
  unlockTime: number
}

export type ClaimableReward = {
  claimableAmount: string
  claimableAmountInUsd?: string
  rewardToken: {
    address: string
    decimals: number
    symbol: string
    usdPrice?: string
  }
}

export type LockMETUserData = {
  amountLocked?: string
  amountLockedInUsd?: string
  lockedPositions?: LockPosition[]
}

export type LockedMETContextType = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metLocker?: any // @TODO: type this? if so, do it in the library
  updateLockedMETData?: () => void
  esMet?: {
    address: string
    balance?: string
    decimals: number
    symbol: string
  }
  exitPenalty?: number
  maximumBoost?: number
  maximumLockPeriod?: number
  minimumLockPeriod?: number
  met?: {
    address: string
    balance?: string
    decimals: number
    symbol: string
    usdPrice?: string
  }
  rewardsData?: RewardsApiResponse
  totalBoosted?: string
  totalLocked?: string
  openOperation?: UseOperationsDrawerOpenHandler<UnlockFormProps>
  closeOperation?: UseOperationsDrawerCloseHandler
  claimables?: ClaimableReward[]
  totalClaimableInUsd?: string
  apy?: string
} & LockMETUserData

export const LockedMETContext = createContext<LockedMETContextType>({})

const LockedMETContextProvider = function ({ initialEsMetInfoData, children }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [metLocker, setMetLocker] = useState<any>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { getLibrary } = useContext<any>(MetronomeContext)
  const { account, isActive: active, provider } = useWeb3React()
  const [shouldUpdate, setShouldUpdate] = useState(false)
  const [userLockedMETData, setUserLockedMETData] = useState<LockMETUserData>({
    amountLocked: '0',
    amountLockedInUsd: '0',
    lockedPositions: []
  })
  const [metBalance, setMetBalance] = useState<string>('0')
  const [esMetBalance, setEsMetBalance] = useState<string>('0')
  const [claimables, setClaimables] = useState<ClaimableReward[]>([])

  const { closeOperation, openOperation, operationDrawer } =
    useLockMETOperationsDrawer()

  const {
    query: { shortName }
  } = useRouter()
  const chainId = useChainId()
  const walletChainId = useWalletChainId()

  const { data: lockMETData } = useSWR(`/api/${shortName}/esmet`, fetcher, {
    fallbackData: initialEsMetInfoData,
    onSuccess() {
      setShouldUpdate(true)
    },
    refreshInterval: parseInt(process.env.NEXT_PUBLIC_REFRESH_INTERVAL) * 1000,
    revalidateOnMount: true
  })

  const rewardsData = useBoostedRewards()

  useEffect(
    function () {
      if (active && walletChainId === chainId) {
        const library = getLibrary(provider.provider)
        createMETLocker(library, {
          chainId,
          from: account
        })
          .then(function (_metLocker) {
            setShouldUpdate(true)
            setMetLocker(_metLocker)
          })
          .catch(console.error)
      } else {
        setMetLocker(undefined)
        setShouldUpdate(false)
      }
    },
    [account, active, chainId, getLibrary, provider?.provider, walletChainId]
  )

  const updateLockedMETData = useCallback(
    function () {
      if (metLocker && shouldUpdate && lockMETData) {
        Promise.all([
          metLocker.getLockedMET(),
          metLocker.getLockedPositions(),
          metLocker.getMETBalance(),
          metLocker.getEsMETBalance(),
          metLocker.getClaimables()
        ])
          .then(function ([
            lockedAmount,
            lockedPositions,
            _metBalance,
            _esMetBalance,
            _claimables
          ]) {
            setShouldUpdate(false)
            setUserLockedMETData({
              amountLocked: Big(fromUnit(lockedAmount)).toFixed(),
              amountLockedInUsd: lockMETData.met.usdPrice
                ? Big(fromUnit(lockedAmount))
                    .times(lockMETData.met.usdPrice)
                    .toFixed()
                : undefined,
              lockedPositions: lockedPositions.map(function (lockedPosition) {
                const positionLockedAmount = Big(
                  fromUnit(lockedPosition.lockedAmount)
                )
                return {
                  boostedAmount: Big(
                    fromUnit(lockedPosition.boostedAmount)
                  ).toFixed(),
                  lockedAmount: positionLockedAmount.toFixed(),
                  lockedAmountInUsd: positionLockedAmount
                    .times(lockMETData.met.usdPrice)
                    .toFixed(),
                  lockedPeriod: parseInt(lockedPosition.lockedPeriod),
                  minimumUnlockTime: parseInt(lockedPosition.minimumUnlockTime),
                  tokenId: parseInt(lockedPosition.tokenId),
                  unlockTime: parseInt(lockedPosition.unlockTime)
                }
              })
            })
            setMetBalance(
              Big(fromUnit(_metBalance, lockMETData.met.decimals)).toFixed()
            )
            setEsMetBalance(
              Big(fromUnit(_esMetBalance, lockMETData.esMet.decimals)).toFixed()
            )

            // Process claimables
            const processedClaimables = _claimables.map(function ({
              claimableAmount,
              rewardToken
            }) {
              const tokenInfo = findTokenByAddress(rewardToken, chainId)
              if (!tokenInfo) {
                console.warn(
                  `Reward token ${rewardToken} not found in token list, defaulting to MET`
                )
              }
              const resolvedTokenInfo = tokenInfo || {
                address: rewardToken,
                decimals: 18,
                symbol: 'MET'
              }
              const claimableInUnit = Big(
                fromUnit(claimableAmount, resolvedTokenInfo.decimals)
              )
              return {
                claimableAmount: claimableInUnit.toFixed(),
                claimableAmountInUsd: lockMETData.met.usdPrice
                  ? claimableInUnit.times(lockMETData.met.usdPrice).toFixed()
                  : undefined,
                rewardToken: {
                  address: rewardToken,
                  decimals: resolvedTokenInfo.decimals,
                  symbol: resolvedTokenInfo.symbol,
                  usdPrice: lockMETData.met.usdPrice
                }
              }
            })
            setClaimables(processedClaimables)
          })
          .catch(console.error)
      }
      return null
    },
    [chainId, lockMETData, metLocker, shouldUpdate]
  )

  useEffect(
    function () {
      if (shouldUpdate) {
        updateLockedMETData()
      }
    },
    [shouldUpdate, updateLockedMETData]
  )

  const totalClaimableInUsd = useMemo(
    () =>
      claimables
        .reduce(
          (acc, { claimableAmountInUsd }) =>
            claimableAmountInUsd ? Big(acc).plus(claimableAmountInUsd) : acc,
          Big(0)
        )
        .toFixed(),
    [claimables]
  )

  const prepareLockMetData = useCallback(
    function () {
      if (!lockMETData) {
        return {}
      }

      const {
        esMet,
        met,
        exitPenalty,
        maximumBoost,
        maximumLockPeriod,
        minimumLockPeriod,
        totalBoosted,
        totalLocked,
        apy
      } = lockMETData

      return {
        esMet: {
          ...esMet,
          ...(esMetBalance ? { balance: esMetBalance } : {})
        },
        exitPenalty: Big(exitPenalty).toNumber(),
        maximumBoost: Big(maximumBoost).toNumber(),
        maximumLockPeriod: Big(maximumLockPeriod).toNumber(),
        met: {
          ...met,
          ...(metBalance ? { balance: metBalance } : {})
        },
        // The minimum lock period is not a valid period and throws an error,
        // so I have to add one more minute to it.
        minimumLockPeriod: Big(minimumLockPeriod).plus(60).toNumber(),
        totalBoosted: Big(totalBoosted).toFixed(),
        totalLocked: Big(totalLocked).toFixed(),
        apy
      }
    },
    [esMetBalance, lockMETData, metBalance]
  )

  if (!initialEsMetInfoData) {
    return <ErrorComponent statusCode={404} />
  }

  return (
    <LockedMETContext.Provider
      value={{
        closeOperation,
        metLocker,
        openOperation,
        updateLockedMETData,
        ...userLockedMETData,
        ...prepareLockMetData(),
        rewardsData,
        claimables,
        totalClaimableInUsd
      }}
    >
      {children}
      {operationDrawer}
    </LockedMETContext.Provider>
  )
}

export default LockedMETContextProvider
